import type { FC } from 'react'
import ArrowLeft from '@sylveraio/untitled-ui-icons-react/build/esm/ArrowLeft'
import ArrowRight from '@sylveraio/untitled-ui-icons-react/build/esm/ArrowRight'
import { Button } from '../../../Button'
import type { PaginationProps } from './types'

export const PaginationPageButton: FC<
  Pick<PaginationProps, 'onPageChange' | 'condensed'> & {
    pageIdx: number
    selected: boolean
  }
> = ({ pageIdx, selected, onPageChange, condensed }) => (
  <Button
    variant="subtle"
    size={condensed ? 'xs' : 'sm'}
    label={(pageIdx + 1).toString()}
    active={selected}
    onClick={() => onPageChange(pageIdx)}
    className={condensed ? 'min-w-8' : 'min-w-9'}
    testId={`pagination-button-${pageIdx}`}
  />
)

export const Pagination: FC<PaginationProps> = ({
  pageCount = 0,
  currentPageId = 0,
  onPageChange,
  condensed,
}) => {
  const finalPageIdx = pageCount - 1
  const pages = [...Array(pageCount)].map((_, i) => i)
  const startRange = currentPageId === 0 ? 0 : currentPageId - 1
  const startPageRange = pages.slice(0, 3)
  const endingPageRange = pages.slice(-3)
  const currentPageRange = pages.slice(startRange, startRange + 3)
  const currentPointerMiddle =
    currentPageId > 1 && currentPageId < pageCount - 2

  return (
    <nav className="flex items-center gap-x-1">
      <Button
        size={condensed ? 'xs' : 'sm'}
        variant="secondary"
        onClick={() => onPageChange(currentPageId - 1)}
        label="Previous"
        iconLeading={<ArrowLeft />}
        disabled={currentPageId === 0}
        testId="pagination-previous"
      />
      {pageCount <= 7 ? (
        pages.map((page) => (
          <PaginationPageButton
            key={`pagination-button-${page}`}
            pageIdx={page}
            onPageChange={onPageChange}
            selected={currentPageId === page}
            condensed={condensed}
          />
        ))
      ) : (
        <>
          {currentPointerMiddle ? (
            <>
              <PaginationPageButton
                key={`pagination-button-0`}
                pageIdx={0}
                onPageChange={onPageChange}
                selected={currentPageId === 0}
                condensed={condensed}
              />
              <Button
                size={condensed ? 'xs' : 'sm'}
                variant="subtle"
                label={'...'}
                onClick={() => console.log('boo')}
                className={condensed ? 'min-w-8' : 'min-w-9'}
                testId="pagination-start-elipsis"
              />
            </>
          ) : (
            startPageRange.map((page) => (
              <PaginationPageButton
                key={`pagination-button-${page}`}
                pageIdx={page}
                onPageChange={onPageChange}
                selected={currentPageId === page}
                condensed={condensed}
              />
            ))
          )}
          {currentPointerMiddle ? (
            currentPageRange.map((page) => (
              <PaginationPageButton
                key={`pagination-button-${page}`}
                pageIdx={page}
                onPageChange={onPageChange}
                selected={currentPageId === page}
                condensed={condensed}
              />
            ))
          ) : (
            <Button
              size={condensed ? 'xs' : 'sm'}
              variant="subtle"
              label={'...'}
              onClick={() => console.log('boo')}
              className={condensed ? 'min-w-8' : 'min-w-9'}
              testId="pagination-mid-elipsis"
            />
          )}
          {currentPointerMiddle ? (
            <>
              <Button
                size={condensed ? 'xs' : 'sm'}
                variant="subtle"
                label={'...'}
                onClick={() => console.log('boo')}
                className={condensed ? 'min-w-8' : 'min-w-9'}
                testId="pagination-end-elipsis"
              />
              <PaginationPageButton
                key={`pagination-button-${finalPageIdx}`}
                pageIdx={finalPageIdx}
                onPageChange={onPageChange}
                selected={currentPageId === finalPageIdx}
                condensed={condensed}
              />
            </>
          ) : (
            endingPageRange.map((page) => (
              <PaginationPageButton
                key={`pagination-button-${page}`}
                pageIdx={page}
                onPageChange={onPageChange}
                selected={currentPageId === page}
                condensed={condensed}
              />
            ))
          )}
        </>
      )}
      <Button
        size={condensed ? 'xs' : 'sm'}
        variant="secondary"
        onClick={() => onPageChange(currentPageId + 1)}
        label="Next"
        iconTrailing={<ArrowRight />}
        disabled={currentPageId === pageCount - 1}
        testId="pagination-next"
      />
    </nav>
  )
}
